import {Action, Selector, State, StateContext} from '@ngxs/store';
import {ProductListItem} from '../../models/product.model';
import {FormState} from '../../models/form-state.model';
import {ProductPgService} from '../../services/product-pg.service';
import {SearchList} from '../../models/search-list.model';
import {NavService} from '../../services/nav.service';
import {Injectable} from '@angular/core';
import {catchError, map, tap} from 'rxjs/operators';
import {clearFormValidationErrors, setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {FormStatus} from '../../enums/file-status.enum';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {ProductPgActions} from './product-pg.actions';
import {ProductPg} from '../../models/product-pg.model';
import {ProductUploadFileError} from '../../models/validation-upload-file-errors';
import {DictionaryItem} from '../../models/dictionary.model';
import {ProductActions} from '../product/product.actions';
import {PolicyTemplateListItem} from '../../models/policy.model';


interface ProductPgStateModel {
    form: FormState;
    list: SearchList<ProductListItem>;
    activeProduct: ProductPg;
    policyTemplatesList: PolicyTemplateListItem[];
    uploadFileErrors: ProductUploadFileError[];
    availableStatuses: DictionaryItem[];
}

@State<ProductPgStateModel>({
    name: ProductPgState.STATE_NAME,
    defaults: {
        form: new FormState(),
        list: new SearchList<ProductListItem>({
            filters: new FormState()
        }),
        activeProduct: null,
        policyTemplatesList: null,
        uploadFileErrors: null,
        availableStatuses: null
    }
})

@Injectable()
export class ProductPgState {

    static readonly STATE_NAME = 'productPg';

    constructor(
        private navService: NavService,
        private productPgService: ProductPgService,
    ) {
    }

    @Selector()
    static activeProduct(state) {
        return state.activeProduct;
    }

    @Selector()
    static formErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static policyTemplatesList(state) {
        return state.policyTemplatesList;
    }

    @Selector()
    static uploadFileErrors(state) {
        return state.uploadFileErrors;
    }

    @Selector()
    static availableStatuses(state) {
        return state.availableStatuses;
    }

    @Action(ProductPgActions.GetOne)
    getProduct(ctx: StateContext<ProductPgStateModel>, {id}: ProductPgActions.GetOne) {
        return this.productPgService.getOne(id).pipe(
            tap((product) => {
                ctx.patchState({
                    activeProduct: new ProductPg(product)
                });
            })
        );
    }

    @Action(ProductPgActions.Duplicate)
    duplicateProduct(ctx: StateContext<ProductPgStateModel>, {id, data}: ProductPgActions.Duplicate) {
        setFormStatus(ctx, FormStatus.PENDING, ProductPgState.STATE_NAME);
        return this.productPgService.duplicate(id, data).subscribe(
            (product) => this.navService.navigateByCode(NavItemCodes.PRODUCT_PG_EDIT, {productId: product.id}),
            ({error}) => ctx.dispatch(new ProductActions.FormFailure(error))
        );
    }

    @Action(ProductPgActions.Merge)
    merge(ctx: StateContext<ProductPgStateModel>, action: ProductPgActions.Merge) {
        return this.productPgService.merge(action.product)
            .pipe(
                map(() => ctx.dispatch(new ProductPgActions.Update(action.product))),
                catchError(({error}) => ctx.dispatch(new ProductPgActions.MergeFailure(error)))
            );
    }

    @Action(ProductPgActions.Update)
    updateProduct(ctx: StateContext<ProductPgStateModel>, action: ProductPgActions.Update) {
        setFormStatus(ctx, FormStatus.PENDING, ProductPgState.STATE_NAME);
        return this.productPgService.update(action.product).pipe(
            tap(() => {
                this.navService.navigateByCode(NavItemCodes.PRODUCT_MANAGEMENT);
            }),
            catchError(({error}) => ctx.dispatch(new ProductPgActions.FormFailure(error)))
        );
    }

    @Action(ProductPgActions.Clear)
    productClear(ctx: StateContext<ProductPgStateModel>) {
        ctx.patchState({
            activeProduct: null,
        });
    }

    @Action(ProductPgActions.ClearValidationErrors)
    productClearValidationErrors(ctx: StateContext<ProductPgStateModel>, {fields = []}: ProductPgActions.ClearValidationErrors) {
        clearFormValidationErrors(ctx, fields);
    }

    @Action(ProductPgActions.FormFailure)
    addProductFailure(ctx: StateContext<ProductPgStateModel>, {validationErrors = []}: ProductPgActions.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, ProductPgState.STATE_NAME);
    }

    @Action(ProductPgActions.MergeFailure)
    uploadFileFailure(ctx: StateContext<ProductPgStateModel>, {uploadFileErrors = []}: ProductPgActions.MergeFailure) {
        ctx.patchState({
            uploadFileErrors: uploadFileErrors?.map(({sheet, message: cellError}) => {
                const [cell, message] = cellError.split(';');
                return { sheet, message, cell };
            })
        });
    }

    @Action(ProductPgActions.ClearFileValidationErrors)
    uploadFileFailureClear(ctx: StateContext<ProductPgStateModel>) {
        ctx.patchState({
            uploadFileErrors: null,
        });
    }

    @Action(ProductPgActions.DownloadFile)
    downloadFile(ctx: StateContext<ProductPgStateModel>, {hash}: ProductPgActions.DownloadFile) {
        return this.productPgService.downloadFile(hash);
    }

    @Action(ProductPgActions.PolicyTemplatesList)
    getPolicyTemplatesListList(ctx: StateContext<ProductPgStateModel>) {
        return this.productPgService.getTemplates().pipe(
            tap((policyTemplatesList: PolicyTemplateListItem[]) => {
                ctx.patchState({
                    policyTemplatesList
                });
            })
        );
    }

    @Action(ProductPgActions.AvailableStatusesList)
    getAvailableStatusesList(ctx: StateContext<ProductPgStateModel>, {id}: ProductPgActions.AvailableStatusesList) {
        return this.productPgService.getStatuses(id).pipe(
            tap((availableStatuses: DictionaryItem[]) => {
                ctx.patchState({
                    availableStatuses
                });
            })
        );
    }
}
